'use client'
import { generateUniqueId } from "@/app/utils/functions";
import { useCallback, useRef } from "react";

export function useQuestionFlow(
  state,
  dispatch,
  saveToStorage,
  removeFromStorage,
  saveDataAgainstUniqueId,
  questions,
  mainFlows
) {
  const { answers, activeMainFlow, activeSubFlow, history } =
    state;

  const handleAnswer = useCallback(
    (slideId, questionIndex, answer, questionId) => {
      dispatch({
        type: "SET_ANSWER",
        slideId,
        questionIndex: questionId || questionIndex,
        answer,
      });

      let updatedAnswers = {};
      if(slideId === 'basicInfo' || questionId === 'basicInfo') {
        updatedAnswers = {
          ...answers,
          basicInfo: {
            ...answer,
          },
        };
      } else {
        updatedAnswers = {
          ...answers,
          [slideId]: {
            ...answers[slideId],
            [questionId || questionIndex]: answer,
          },
        };
      }

      const data = {
        ...answers,
        [slideId]: {
          ...answers[slideId],
          [questionId || questionIndex]: answer,
        },
      };

      saveToStorage("userAnswers", data);
      if(updatedAnswers?.basicInfo?.basicInfo) {
        saveToStorage("userAnswers", {...updatedAnswers, basicInfo: {
          ...updatedAnswers.basicInfo.basicInfo
        }});
      } else {
        saveToStorage("userAnswers", updatedAnswers);
      }

      saveDataAgainstUniqueId();
      if (!answer) return null;

      const currentFlow = mainFlows[activeMainFlow];
      const currentSlide = questions?.[currentFlow]?.[activeSubFlow];
      if (currentSlide.slide?.[questionIndex]?.autoAdvance) {
        moveToNextQuestion(
          slideId,
          answer,
          questions,
          mainFlows,
          updatedAnswers
        );
      }
    },
    [
      answers,
      activeMainFlow,
      questions,
      mainFlows,
      activeSubFlow,
      history,
      dispatch,
      saveToStorage,
    ]
  );

  const moveToNextQuestion = useCallback(
    async (slideId, answer, questions, mainFlows, updatedAnswers = answers) => {
      dispatch({ type: "SET_FADE", fadeIn: false });

      const currentSlide = questions[mainFlows[activeMainFlow]][activeSubFlow];

      let processedAnswer = null;
      if (
        currentSlide?.callBack &&
        typeof currentSlide?.callBack === "function"
      ) {
        const returnedValue = await currentSlide.callBack(answer);
        if (returnedValue?.hasError) return false;
        dispatch({
          type: "SET_API_RESPONSE",
          slideId,
          response: returnedValue.data,
        });
        processedAnswer = returnedValue?.validated;
      }

      const lastHistoryItem = history[history.length - 1];
      const currentPosition = {
        mainFlow: activeMainFlow,
        subFlow: activeSubFlow,
      };
      const isCurrentPositionLastInHistory =
        lastHistoryItem &&
        lastHistoryItem.mainFlow === currentPosition.mainFlow &&
        lastHistoryItem.subFlow === currentPosition.subFlow;

      if (!isCurrentPositionLastInHistory) {
        const newHistory = [...history, currentPosition];
        dispatch({ type: "SET_HISTORY", history: newHistory });
        saveToStorage("userHistory", newHistory);
      }

      setTimeout(async () => {
        const currentFlow = mainFlows[activeMainFlow];
        const currentSlide = questions[currentFlow][activeSubFlow];
        const currentSlideAnswers = updatedAnswers[currentSlide.id] || {};

        const answersArray = Object.keys(currentSlideAnswers)
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => currentSlideAnswers[key]);

        const finalAnswer =
          processedAnswer !== null ? [processedAnswer] : answersArray;
        let nextSlideId;
        if (typeof currentSlide.next === "function") {
          nextSlideId = await currentSlide.next(finalAnswer);
        } else {
          nextSlideId = currentSlide.next;
        }

        if (nextSlideId == "removeall") {
          return reset();
        }

        const currentFlowQuestions = questions[currentFlow];
        const nextSlideIndex = currentFlowQuestions.findIndex(
          (q) => q.id === nextSlideId
        );

        if (nextSlideIndex !== -1) {
          const newPosition = {
            mainFlow: activeMainFlow,
            subFlow: nextSlideIndex,
          };
          dispatch({
            type: "SET_POSITION",
            ...newPosition,
          });
          saveToStorage("userPosition", newPosition);

          const lastHistoryItem = history[history.length - 1];
          if (
            !lastHistoryItem ||
            lastHistoryItem.mainFlow !== newPosition.mainFlow ||
            lastHistoryItem.subFlow !== newPosition.subFlow
          ) {
            const updatedHistory = [...history, newPosition];
            dispatch({ type: "SET_HISTORY", history: updatedHistory });
            saveToStorage("userHistory", updatedHistory);
          }
        } else {
          for (const key in questions) {
            const slide = questions[key];
            const nextSlideIndexInNewFlow = slide.findIndex(
              (q) => q.id === nextSlideId
            );
            const newSubFlow =
              nextSlideIndexInNewFlow !== -1 ? nextSlideIndexInNewFlow : 0;
            if (nextSlideIndexInNewFlow !== -1) {
              const mainFlowIndex = mainFlows.findIndex((val) => val === key);
              if (mainFlowIndex === -1) return;
              const newPosition = {
                mainFlow: mainFlowIndex,
                subFlow: newSubFlow,
              };
              dispatch({
                type: "SET_POSITION",
                ...newPosition,
              });
              saveToStorage("userPosition", newPosition);

              const lastHistoryItem = history[history.length - 1];
              if (
                !lastHistoryItem ||
                lastHistoryItem.mainFlow !== newPosition.mainFlow ||
                lastHistoryItem.subFlow !== newPosition.subFlow
              ) {
                const updatedHistory = [...history, newPosition];
                dispatch({ type: "SET_HISTORY", history: updatedHistory });
                saveToStorage("userHistory", updatedHistory);
              }
              break; // Exit the loop once we've found and processed the next slide
            }
          }
        }
        dispatch({ type: "SET_FADE", fadeIn: true });
      }, 300);
    },
    [activeMainFlow, activeSubFlow, history, dispatch, saveToStorage, answers]
  );

  const goBack = useCallback(() => {
    if (history.length > 0) {
      dispatch({ type: "SET_FADE", fadeIn: false });
      setTimeout(() => {
        const newHistory = history.slice(0, -1);
        let prevState = newHistory[newHistory.length - 1];
        if (
          prevState &&
          typeof prevState.mainFlow !== "undefined" &&
          typeof prevState.subFlow !== "undefined"
        ) {
          dispatch({
            type: "SET_POSITION",
            mainFlow: prevState.mainFlow,
            subFlow: prevState.subFlow,
          });
          saveToStorage("userPosition", {
            mainFlow: prevState.mainFlow,
            subFlow: prevState.subFlow,
          });
          dispatch({ type: "SET_HISTORY", history: newHistory });
          saveToStorage("userHistory", newHistory);
        } else {
          const newHistory = { mainFlow: 0, subFlow: 0 };
          dispatch({ type: "SET_HISTORY", history: [] });
          saveToStorage("userHistory", []);
          dispatch({
            type: "SET_POSITION",
            ...newHistory,
          });
          saveToStorage("userPosition", {
            ...newHistory,
          });

          console.error("Invalid previous state in history");
        }
        dispatch({ type: "SET_FADE", fadeIn: true });
      }, 300);
    } else {
      console.log("Already at the first slide");
    }
  }, [history, dispatch, saveToStorage]);

  const reset = useCallback(() => {
    dispatch({ type: "SET_FADE", fadeIn: false });
    setTimeout(() => {
      dispatch({ type: "RESET" });
      removeFromStorage("userAnswers");
      removeFromStorage("userPosition");
      removeFromStorage("userHistory");
      removeFromStorage("sessionId");
      // dispatch({ type: "SET_FADE", fadeIn: true });
    }, 300);
  }, [dispatch, removeFromStorage]);

  return { handleAnswer, moveToNextQuestion, goBack, reset };
}

export const useLocalStorage = () => {
  const loadFromStorage = (key) => {
    if (typeof window !== "undefined") {
      const item = localStorage.getItem(key);
      if (item) {
        return JSON.parse(item);
      }
      return null;
    }
    return null;
  };

  const saveToStorage = (key, value) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(key, JSON.stringify(value));
    } else {
      try {
        localStorage.setItem(key, JSON.stringify(value)); 
      } catch(e) {
        console.log(`Error Saving to Storage -> "${key}: ${value}"`, e);
      }
    }
  };

  const removeFromStorage = (key) => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(key);
    }
  };

  const sessionId = loadFromStorage("sessionId");

  const saveDataAgainstUniqueId = () => {
    const claims = loadFromStorage("claims");
    const vehicleData = loadFromStorage("userAnswers");
    const currentId = loadFromStorage("currentId");
    const email =
      claims?.email ||
      (vehicleData?.["basicInfo"]
        ? vehicleData["basicInfo"]["yourEmail"]
        : vehicleData?.["yourEmail"]?.[0]);
    // const existingClaims = loadFromStorage("claims") || {};
    const generatedId = currentId || generateUniqueId();
    saveToStorage("currentId", generatedId);
    const modifiedVehicleData = {
      [email]: {
        // ...existingClaims?.[currentEmail],
        [generatedId]: {
          surname:
            claims?.surname ||
            (vehicleData?.["basicInfo"]
              ? vehicleData?.["basicInfo"]["surname"]
              : vehicleData?.["surname"]?.[0]),
          firstName:
            claims?.surname ||
            (vehicleData?.["basicInfo"]
              ? vehicleData?.["basicInfo"]["givenName"]
              : vehicleData?.["givenName"]?.[0]),
          dob:
            claims?.surname ||
            (vehicleData?.["basicInfo"]
              ? vehicleData?.["basicInfo"]["dateOfBirth"]
              : vehicleData?.["dateOfBirth"]?.[0]),
          lender: claims?.surname || vehicleData?.["lender"]?.[0],
          carMake: claims?.surname || vehicleData?.["carMake"]?.[0],
          address:
            claims?.surname ||
            (vehicleData?.["basicInfo"]
              ? vehicleData?.["basicInfo"]["address"]?.address
              : vehicleData?.["address.currentAddress"]?.[0]?.address ||
                vehicleData?.["address"]?.[0]?.address),
          email: email,
          phone: claims?.surname || vehicleData?.["yourPhone"]?.[0],
          docusign: claims?.surname || vehicleData?.["docusign"]?.[0],
          vehicleRegistrationNumber:
            claims?.surname || vehicleData?.["vehicleRegistrationNumber"]?.[0],
        },
      },
    };
    saveToStorage("claims", modifiedVehicleData);
  };

  return {
    loadFromStorage,
    saveToStorage,
    removeFromStorage,
    saveDataAgainstUniqueId,
    sessionId,
  };
};

export const useSessionStorage = () => {
  const loadFromSessionStorage = (key) => {
    if (typeof window !== "undefined") {
      const item = sessionStorage.getItem(key);
      if (item) {
        return item;
      }
      return null;
    }
    return null;
  };

  const saveToSessionStorage = (key, value) => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(key, value);
    }
  }

  return {
    loadFromSessionStorage,
    saveToSessionStorage
  }
}

export const useDebounce = () => {
  const timeoutRef = useRef(null);

  const addDebounce = (callBack) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(callBack, 1000);
  };

  return { addDebounce };
};
